import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PenLine, Sparkles, Check, X, ChevronDown, ChevronUp, AlertTriangle, CircleCheck, Loader2, Split, ArrowRight, CheckSquare, Square, Flag, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { HierarchyCollisionResolver } from '../components/HierarchyCollisionResolver';
import { ContextualHelp } from '../components/ContextualHelp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PriorityBadge = ({ priority }) => {
  const styles = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-slate-100 text-slate-600'
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[priority] || styles.low}`}>
      {priority}
    </span>
  );
};

const REVIEW_REASON_LABELS = {
  hierarchy_collision_l2_eq_l3: { label: 'Hierarchy collision (L2 ↔ L3)', tone: 'bg-rose-100 text-rose-700' },
  llm_timeout: { label: 'LLM timeout', tone: 'bg-amber-100 text-amber-700' },
  llm_error: { label: 'LLM error', tone: 'bg-orange-100 text-orange-700' },
  unspecified: { label: 'Flagged for review', tone: 'bg-slate-100 text-slate-700' },
};

const ReviewReasonBadge = ({ reason }) => {
  const meta = REVIEW_REASON_LABELS[reason] || { label: reason || 'Flagged', tone: 'bg-slate-100 text-slate-700' };
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${meta.tone}`}
      data-testid={`review-reason-${reason || 'unspecified'}`}
      title={`Flagged by Vocabulary Lock: ${meta.label}`}
    >
      <Flag size={10} />
      {meta.label}
    </span>
  );
};

const CorrectionForm = ({ record, suggestion, token, onCorrected }) => {
  const [category, setCategory] = useState(suggestion?.suggested_category || record.category || '');
  const [supplier, setSupplier] = useState(suggestion?.suggested_supplier || record.supplier || '');
  const [notes, setNotes] = useState('');
  const [confidence, setConfidence] = useState(7);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!category && !supplier) {
      toast.error('Please enter a corrected category or supplier');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/spend/correct/${record.id}`, {
        corrected_category: category !== record.category ? category : null,
        corrected_supplier: supplier !== record.supplier ? supplier : null,
        feedback_notes: notes,
        confidence_rating: confidence
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Correction submitted');
      onCorrected(record.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit correction');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-t border-slate-100 pt-4 mt-3 space-y-3" data-testid={`correction-form-${record.id}`}>
      {suggestion?.reasoning && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm">
          <Sparkles size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-semibold text-amber-800">AI Recommendation: </span>
            <span className="text-amber-700">{suggestion.reasoning}</span>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Category</label>
          <input data-testid={`correction-category-${record.id}`} value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Corrected category" />
          {record.category && category !== record.category && (
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <span className="line-through">{record.category}</span>
              <ArrowRight size={10} />
              <span className="text-blue-600 font-medium">{category}</span>
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Supplier</label>
          <input data-testid={`correction-supplier-${record.id}`} value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Corrected supplier name" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Notes (optional)</label>
        <input data-testid={`correction-notes-${record.id}`} value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Why is this correction needed?" />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-600">Confidence:</label>
          <input type="range" min="1" max="10" value={confidence}
            onChange={(e) => setConfidence(parseInt(e.target.value))}
            className="w-24 h-1.5 accent-blue-600" />
          <span className="text-xs text-slate-500 w-6">{confidence}/10</span>
        </div>
        <Button data-testid={`submit-correction-${record.id}`} onClick={handleSubmit}
          disabled={submitting} size="sm"
          className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Submit Correction
        </Button>
      </div>
    </div>
  );
};

const RecordRow = ({ record, suggestion, token, onCorrected, selected, onToggleSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const isSplit = record.is_split_transaction;
  const needsCorrection = suggestion?.needs_correction;
  const needsReview = record.needs_review === true;
  const conf = (record.classification_confidence || 0) * 100;
  // AI's one-sentence rationale captured at classification time (Feb 2026).
  // Not every record has one — older records / fallback "Other" records will be empty.
  const reasoning = (record.classification_reasoning || '').trim();

  return (
    <div data-testid={`record-row-${record.id}`}
      className={`border rounded-sm transition-all ${
        selected ? 'border-blue-400 bg-blue-50/40' :
        needsReview ? 'border-rose-300 bg-rose-50/30' :
        needsCorrection ? 'border-amber-300 bg-amber-50/30' : 'border-slate-200 bg-white'
      }`}>
      <div className="flex items-center p-4 gap-3">
        <button onClick={(e) => { e.stopPropagation(); onToggleSelect(record.id); }}
          className="flex-shrink-0" data-testid={`select-record-${record.id}`}>
          {selected
            ? <CheckSquare size={18} className="text-blue-600" />
            : <Square size={18} className="text-slate-300 hover:text-slate-500" />}
        </button>
        <button onClick={() => setExpanded(!expanded)}
          className="flex-1 text-left flex items-center gap-3 min-w-0"
          data-testid={`toggle-record-${record.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-slate-900 truncate">{record.description}</span>
              {isSplit && (
                <span className="flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full flex-shrink-0">
                  <Split size={10} /> Split {record.split_index}/{record.total_splits}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span>{record.supplier}</span>
              <span className="font-medium text-slate-700">{record.currency || 'USD'} {Number(record.amount).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
              <span>{record.date}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className={`text-xs px-2 py-1 rounded-sm font-medium ${
              record.category ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500'
            }`}>{record.category || 'Uncategorized'}</span>
            <span className={`text-xs font-medium ${
              conf >= 90 ? 'text-green-600' : conf >= 70 ? 'text-amber-600' : 'text-red-600'
            }`}>{conf.toFixed(0)}%</span>
            {reasoning && (
              <span
                title={`Why this category? ${reasoning}`}
                className="text-slate-400 hover:text-cyan-600 cursor-help flex-shrink-0"
                data-testid={`reasoning-hint-${record.id}`}>
                <Info size={13} />
              </span>
            )}
            {needsCorrection && <PriorityBadge priority={suggestion.priority} />}
            {needsReview && <ReviewReasonBadge reason={record.review_reason} />}
            {record.user_corrected && (
              <span className="flex items-center gap-1 text-xs text-green-600">
                <CircleCheck size={12} /> Corrected
              </span>
            )}
            {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </div>
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pl-12 space-y-3">
          {reasoning ? (
            <div
              className="flex items-start gap-2 text-xs bg-slate-50 border border-slate-200 rounded-sm px-3 py-2"
              data-testid={`reasoning-detail-${record.id}`}>
              <Info size={14} className="text-cyan-600 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold text-slate-600 mr-1.5">Why this category?</span>
                <span className="text-slate-700">{reasoning}</span>
              </div>
            </div>
          ) : (
            <div
              className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-sm px-3 py-2"
              data-testid={`reasoning-missing-${record.id}`}>
              <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <span className="text-amber-800">
                No AI reasoning was captured for this record — it may have been classified before reasoning was added, or fell back to the default bucket. Correcting it will feed future runs.
              </span>
            </div>
          )}
          <CorrectionForm record={record} suggestion={suggestion}
            token={token} onCorrected={onCorrected} />
        </div>
      )}
    </div>
  );
};

// Bulk Correction Floating Bar
const BulkCorrectionBar = ({ selectedCount, onApply, onClear }) => {
  const [category, setCategory] = useState('');
  const [supplier, setSupplier] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleApply = async () => {
    if (!category && !supplier) {
      toast.error('Enter a category or supplier for bulk correction');
      return;
    }
    setSubmitting(true);
    await onApply(category, supplier);
    setSubmitting(false);
    setCategory('');
    setSupplier('');
  };

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#0F172A] text-white rounded-lg shadow-2xl px-6 py-4 flex items-center gap-4 max-w-3xl w-full"
      data-testid="bulk-correction-bar">
      <div className="flex items-center gap-2 text-sm font-medium flex-shrink-0">
        <CheckSquare size={18} className="text-blue-400" />
        <span>{selectedCount} selected</span>
      </div>
      <div className="h-8 w-px bg-slate-600" />
      <input value={category} onChange={(e) => setCategory(e.target.value)}
        placeholder="New category for all"
        className="flex-1 bg-slate-800 border border-slate-600 rounded-sm px-3 py-1.5 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid="bulk-category-input" />
      <input value={supplier} onChange={(e) => setSupplier(e.target.value)}
        placeholder="New supplier (optional)"
        className="flex-1 bg-slate-800 border border-slate-600 rounded-sm px-3 py-1.5 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid="bulk-supplier-input" />
      <Button onClick={handleApply} disabled={submitting} size="sm"
        className="bg-blue-600 hover:bg-blue-700 text-white gap-1" data-testid="bulk-apply-button">
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
        Apply
      </Button>
      <button onClick={onClear} className="text-slate-400 hover:text-white" data-testid="bulk-clear-button">
        <X size={18} />
      </button>
    </div>
  );
};

export const Corrections = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState(null);
  const [records, setRecords] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [correctedIds, setCorrectedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filter, setFilter] = useState('all');
  const [reviewMeta, setReviewMeta] = useState({ needs_review_count: 0, review_reasons: [] });

  useEffect(() => { fetchSessions(); }, []);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/spend/processing-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const classified = res.data.sessions.filter(s => s.status === 'classified');
      setSessions(classified);
      if (classified.length > 0) loadSession(classified[0].id);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (uploadId) => {
    setSelectedUploadId(uploadId);
    setRecords([]);
    setSuggestions([]);
    setCorrectedIds(new Set());
    setSelectedIds(new Set());
    setFilter('all');
    setReviewMeta({ needs_review_count: 0, review_reasons: [] });
    try {
      const [recRes, metaRes] = await Promise.all([
        axios.get(`${API}/spend/records?upload_id=${uploadId}&per_page=1000`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/spend/records-meta?upload_id=${uploadId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} })),
      ]);
      setRecords(recRes.data.records);
      setReviewMeta({
        needs_review_count: metaRes.data?.needs_review_count || 0,
        review_reasons: metaRes.data?.review_reasons || [],
      });
      fetchSuggestions(uploadId);
    } catch (err) {
      toast.error('Failed to load records');
    }
  };

  const fetchSuggestions = async (uploadId) => {
    setLoadingSuggestions(true);
    try {
      const res = await axios.post(`${API}/spend/correction-suggestions/${uploadId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleCorrected = useCallback((recordId) => {
    setCorrectedIds(prev => new Set([...prev, recordId]));
    setSelectedIds(prev => { const n = new Set(prev); n.delete(recordId); return n; });
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }, []);

  const selectAll = () => {
    setSelectedIds(new Set(filteredRecords.map(r => r.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkApply = async (category, supplier) => {
    const ids = [...selectedIds];
    try {
      const res = await axios.post(`${API}/spend/bulk-correct`, {
        record_ids: ids,
        corrected_category: category || null,
        corrected_supplier: supplier || null,
        feedback_notes: 'Bulk correction',
        confidence_rating: 8
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      ids.forEach(id => setCorrectedIds(prev => new Set([...prev, id])));
      setSelectedIds(new Set());
      // Reload records
      const rRes = await axios.get(`${API}/spend/records?upload_id=${selectedUploadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecords(rRes.data.records);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk correction failed');
    }
  };

  const suggestionMap = {};
  suggestions.forEach(s => { suggestionMap[s.id] = s; });
  const needsCorrectionCount = suggestions.filter(s => s.needs_correction).length;
  const needsReviewCount = reviewMeta.needs_review_count;

  const filteredRecords = records.filter(r => {
    if (filter === 'needs_correction') return suggestionMap[r.id]?.needs_correction && !correctedIds.has(r.id);
    if (filter === 'needs_review') return r.needs_review === true;
    if (filter === 'corrected') return r.user_corrected || correctedIds.has(r.id);
    return true;
  });

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <Loader2 size={32} className="animate-spin text-slate-400" />
    </div>;
  }

  if (sessions.length === 0) {
    return <div className="flex items-center justify-center h-96">
      <div className="text-center">
        <PenLine className="mx-auto mb-4 text-slate-300" size={48} />
        <p className="text-slate-600">No classified data yet. Upload and process data first.</p>
      </div>
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="corrections-page">
      <ContextualHelp guideId="corrections" />
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">Data Corrections</h1>
        <p className="text-slate-600">Review AI classifications and submit corrections to improve model accuracy</p>
      </div>

      {/* Session Selector + Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-700">Session:</label>
          <select data-testid="session-selector" value={selectedUploadId || ''}
            onChange={(e) => loadSession(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            {sessions.map(s => (
              <option key={s.id} value={s.id}>{s.filename} ({s.total_records} records)</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1 bg-slate-100 rounded-sm p-0.5">
          {[
            { key: 'all', label: 'All Records' },
            { key: 'needs_correction', label: `Needs Correction (${needsCorrectionCount})` },
            { key: 'needs_review', label: `Needs Review (${needsReviewCount})` },
            { key: 'corrected', label: 'Corrected' }
          ].map(f => (
            <button key={f.key} data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                filter === f.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}>{f.label}</button>
          ))}
        </div>

        {/* Select All / Clear */}
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={selectAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            data-testid="select-all-button">Select All</button>
          {selectedIds.size > 0 && (
            <button onClick={clearSelection} className="text-xs text-slate-500 hover:text-slate-700"
              data-testid="clear-selection-button">Clear ({selectedIds.size})</button>
          )}
        </div>
      </div>

      {/* Vocabulary-Lock Review Summary */}
      {needsReviewCount > 0 && (
        <div className="flex items-start gap-3 p-4 bg-rose-50 border border-rose-200 rounded-sm" data-testid="needs-review-summary">
          <Flag size={20} className="text-rose-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-800">
              {needsReviewCount} record(s) flagged by Vocabulary Lock
            </p>
            <p className="text-xs text-rose-700 mt-1">
              {reviewMeta.review_reasons.length > 0 && (
                <>Reasons: {reviewMeta.review_reasons.map(r => {
                  const meta = REVIEW_REASON_LABELS[r.reason] || { label: r.reason };
                  return `${meta.label} (${r.count})`;
                }).join(' · ')}. </>
              )}
              Fix these to lift your Trust Score.
            </p>
          </div>
          <Button size="sm" variant="outline"
            className="flex-shrink-0 border-rose-400 text-rose-700 hover:bg-rose-100"
            onClick={() => setFilter('needs_review')} data-testid="show-needs-review-button">
            Show Flagged
          </Button>
        </div>
      )}

      {/* AI Suggestions Summary */}
      {loadingSuggestions ? (
        <div className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-sm">
          <Loader2 size={16} className="animate-spin text-blue-600" />
          <span className="text-sm text-blue-700">Generating AI correction recommendations...</span>
        </div>
      ) : needsCorrectionCount > 0 && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-sm" data-testid="suggestions-summary">
          <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              AI found {needsCorrectionCount} record(s) that may need correction
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Use checkboxes to select multiple records for bulk correction, or expand each to correct individually.
            </p>
          </div>
          <Button size="sm" variant="outline"
            className="flex-shrink-0 border-amber-400 text-amber-700 hover:bg-amber-100"
            onClick={() => setFilter('needs_correction')} data-testid="show-flagged-button">
            Show Flagged
          </Button>
        </div>
      )}

      {/* Hierarchy Collision Resolver — only visible on the Needs Review tab */}
      {filter === 'needs_review' && selectedUploadId && (
        <HierarchyCollisionResolver
          token={token}
          uploadId={selectedUploadId}
          onResolved={() => loadSession(selectedUploadId)}
        />
      )}

      {/* Records List */}
      <div className="space-y-2" data-testid="records-list">
        {filteredRecords.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            {filter === 'needs_correction' ? 'No records flagged by AI for correction' :
             filter === 'needs_review' ? 'No records flagged for review by Vocabulary Lock' :
             filter === 'corrected' ? 'No corrections submitted yet' : 'No records found'}
          </div>
        ) : (
          filteredRecords.map(r => (
            <RecordRow key={r.id} record={r} suggestion={suggestionMap[r.id]}
              token={token} onCorrected={handleCorrected}
              selected={selectedIds.has(r.id)} onToggleSelect={toggleSelect} />
          ))
        )}
      </div>

      <div className="text-xs text-slate-400 text-center pt-4">
        Showing {filteredRecords.length} of {records.length} records
      </div>

      {/* Bulk Correction Floating Bar */}
      {selectedIds.size > 0 && (
        <BulkCorrectionBar selectedCount={selectedIds.size}
          onApply={handleBulkApply} onClear={clearSelection} />
      )}
    </div>
  );
};
